import {
  BrowserRouter as Router,
  Redirect,
  Route,
  Switch,
} from 'react-router-dom';
import { AuthProvider } from '../contexts/auth';
import AuthenticatedRoute from '../components/Routes/AuthenticatedRoute';
import AuthLoadingRoute from '../components/Routes/AuthLoadingRoute';
import NotFound from '../pages/NotFound';
import {
  ADMIN_ROOT_PATH,
  ROUTES,
  PROSUMER_PUBLIC_UPSELL_ROOT_PATH,
  PUBLIC_ROOT_PATH,
  PREVIEW_ROOT_PATH,
} from '../routing';
import { TEAM_LOGIN_VARIANT } from '../pages/Login/loginConstants';
import Home from '../pages/Home/Home';
import Playground from '../pages/Playground';
import ProsumerQueryParamsHandlers from '../components/ProsumerQueryParamsHandlers';
import { AIWorkflowProvider } from '../contexts/ai-workflow';
import { SoloOnboardingProvider } from '../components/SoloOnboarding/context';
import AuthCallback from '../pages/AuthCallback/AuthCallback';
import ConfirmEmailPage from '../pages/ConfirmEmail';
import LoginPage from '../pages/Login/Login';
import SoloOnboarding from '../pages/SoloOnboarding';
import Unsubscribe from '../pages/Unsubscribe';
import AppUserUpsell from '../pages/AppUserUpsell';
import EmbeddablePythonPlayground from '../pages/PythonPlayground';
import EmbeddableSQLPlayground from '../pages/SQLPlayground';
import { ADMIN_ROUTES } from './routes/AdminRoutes';
import { PREVIEW_CONTENT_ROUTES } from './routes/PreviewRoutes';
import {
  PROSUMER_PUBLIC_UPSSELL_ROUTES,
  PROSUMER_ROUTES,
} from './routes/ProsumerRoutes';
import { TEAM_ROUTES } from './routes/TeamRoutes';
import useTrackNavigationChanges from '../hooks/use-track-navigation-changes';
import ApplicationWrapper from './ApplicationWrapper';
import { ProsumerPublicUpsellProvider } from '../contexts/prosumer-public-upsell';

export default function Application() {
  return (
    <ApplicationWrapper>
      <Router>
        <ApplicationSwitch />
      </Router>
    </ApplicationWrapper>
  );
}

const ApplicationSwitch = () => {
  useTrackNavigationChanges();
  return (
    <Switch>
      <Route path={PUBLIC_ROOT_PATH}>
        <PublicRoutes />
      </Route>
      <Route path="/">
        <AuthProvider>
          <AuthenticatedApp />
        </AuthProvider>
      </Route>
    </Switch>
  );
};

function PublicRoutes() {
  return (
    <Switch>
      <Route exact path={ROUTES.PUBLIC.SQL_PLAYROUND.PATH}>
        <Playground />
      </Route>
      <Route path={ROUTES.PUBLIC.PYTHON_PLAYROUND_EMBED.PATH}>
        <EmbeddablePythonPlayground />
      </Route>
      <Route path={ROUTES.PUBLIC.SQL_PLAYROUND_EMBED.PATH}>
        <EmbeddableSQLPlayground />
      </Route>
      {/* not found fallback */}
      <Route>
        <NotFound />
      </Route>
    </Switch>
  );
}

function AuthenticatedApp() {
  return (
    <Switch>
      <AuthLoadingRoute exact path={ROUTES.AUTH.LOGIN.PATH}>
        <LoginPage />
      </AuthLoadingRoute>
      <AuthLoadingRoute exact path={ROUTES.TEAMS.LOGIN.PATH}>
        <LoginPage isTeamsOnboard />
      </AuthLoadingRoute>
      <AuthLoadingRoute exact path={ROUTES.PROSUMER.APP_USER_UPSELL.PATH}>
        <AppUserUpsell />
      </AuthLoadingRoute>
      <AuthLoadingRoute exact path={ROUTES.AUTH.CONFIRM_EMAIL.PATH}>
        <ConfirmEmailPage />
      </AuthLoadingRoute>
      <Route exact path={ROUTES.AUTH.CALLBACK.PATH}>
        <AuthCallback />
      </Route>
      <Route exact path="/prosumer/onboarding">
        <Redirect
          to={{
            pathname: ROUTES.PROSUMER.ONBOARDING.getPath(),
            search: window.location.search,
          }}
        />
      </Route>
      <Route exact path={ROUTES.PROSUMER.ONBOARDING.PATH}>
        <SoloOnboardingProvider>
          <SoloOnboarding />
        </SoloOnboardingProvider>
      </Route>

      <Route exact path={ROUTES.PROSUMER.UNSUBSCRIBE.PATH}>
        <Unsubscribe />
      </Route>
      <Route path={PROSUMER_PUBLIC_UPSELL_ROOT_PATH}>
        <ProsumerPublicUpsellProvider>
          <Switch>
            {[...PROSUMER_PUBLIC_UPSSELL_ROUTES]}
            <Route>
              <NotFound />
            </Route>
          </Switch>
        </ProsumerPublicUpsellProvider>
      </Route>

      <Route path={ADMIN_ROOT_PATH}>
        <Switch>
          {[...ADMIN_ROUTES]}
          <Route>
            <NotFound />
          </Route>
        </Switch>
      </Route>

      <Route path="/">
        <AIEnabledRoutes />
      </Route>
    </Switch>
  );
}

function AIEnabledRoutes() {
  return (
    <AIWorkflowProvider>
      <ProsumerQueryParamsHandlers />
      <Switch>
        <AuthenticatedRoute exact path={ROUTES.HOME.PATH}>
          <Home />
        </AuthenticatedRoute>
        <Route path={PREVIEW_ROOT_PATH}>
          <Switch>
            {[...PREVIEW_CONTENT_ROUTES]}
            <Route path="*">
              <NotFound />
            </Route>
          </Switch>
        </Route>
        <Route path="/">
          <Switch>
            <AuthenticatedRoute exact path={ROUTES.HOME.PATH}>
              <Home />
            </AuthenticatedRoute>
            {/* Prosumer routes must come before team routes: the team
                activity route is a greedy top-level pattern
                (/:trackSlug/missions/:missionSlug/activities/:activitySlug)
                that would otherwise capture /prosumer/missions/... URLs
                (trackSlug="prosumer") and hit the teams endpoint, 404ing with
                "cannot find that track". */}
            {[...PROSUMER_ROUTES]}
            {[...TEAM_ROUTES]}
            <Route
              exact
              path={ROUTES.TEAMS.TEST_INVITE.PATH}
              render={(props) => (
                <Redirect
                  to={{
                    pathname: ROUTES.AUTH.LOGIN.getPath(),
                    state: {
                      variant: TEAM_LOGIN_VARIANT.TEST,
                      companySlug: props.match.params.companySlug,
                    },
                  }}
                />
              )}
            />
            <Route path="*">
              <NotFound />
            </Route>
          </Switch>
        </Route>
      </Switch>
    </AIWorkflowProvider>
  );
}
